import type { RefObject } from 'react';
import { format } from 'date-fns';

import { ACTION_PLAN_STATUS_MAP } from 'src/constants/actionPlan';
import { LANGUAGE, LANGUAGE_MAP } from 'src/constants/language';
import {
  ORDER_STATUS_MAP,
  REPORT_STATUS_MAP,
  ReportFileType,
} from 'src/constants/report';
import type { ModuleType } from 'src/types/modulePermission';
import type { Report } from 'src/types/report';
import type { LanguageInfo } from 'src/types/reportConfig';
import i18next from 'src/services/i18n/i18next';
import { modulePaths, routePaths } from 'src/services/routing/routePaths';

/**
 * This file contains app wide helpers.
 */

// Crazy stuff happening in IE & Edge.
// If we just use LANGUAGE_MAP in getLanguageName, we will always get an empty array.
const defaultLanguageMap = [...LANGUAGE_MAP];

/**
 * Get attribute of object safe or undefined.
 * example usage: get(() => this.props.foo.bar.awesome)
 * if foo or bar do not exist, the javascript code will not crash. We will only get the default return value.
 * see: https://silvantroxler.ch/2017/avoid-cannot-read-property-of-undefined/
 * @param fn
 * @param defaultReturnValue the value to return if the fn call fails for any reason
 */
export const get = (fn: () => any, defaultReturnValue = null) => {
  try {
    return fn();
  } catch (_e) {
    return defaultReturnValue;
  }
};

/**
 * This function transfers error messages we receive from the api to the format of formik. Formik's error property
 * simply holds an object with keys equal to the names of the fields, e.g. {email: 'wrong', password:'big error'}.
 * If for some reason our api returns errors with different names, we can make a mapping here.
 */
export const transformApiErrors = (data: any) => ({
  ...data,
  nonFieldErrors: get(() => data.non_field_errors),
});

/**
 * @param id - an id for searching the matching language abbreviation inside languageMap
 * @param languageMap - an array ob language objects containing an id and abbreviation key
 * @returns - a matching language abbreviation for id or NULL
 */
export const getLanguageAbbreviation = (
  id: number,
  languageMap = defaultLanguageMap,
) => {
  const selectedLanguage = languageMap.find((language) => language.code === id);

  return typeof selectedLanguage === 'object' &&
    Object.prototype.hasOwnProperty.call(selectedLanguage, 'abbreviation')
    ? selectedLanguage.abbreviation
    : null;
};

/**
 * @returns - full locale
 */
export const getCurrentLocale = () =>
  i18next.language === 'de' ? 'de-DE' : 'en-US';

/**
 * @param id - an id for searching the matching language name inside languageMap
 * @param languageMap - an array ob language objects containing an id and name key
 * @returns - a matching language name for id or NULL
 */
export const getLanguageName = (
  id: number | string,
  languageMap = defaultLanguageMap,
) => {
  const selectedLanguage = languageMap.find((language) => language.code === id);
  return typeof selectedLanguage === 'object' &&
    Object.prototype.hasOwnProperty.call(selectedLanguage, 'abbreviation')
    ? i18next.t(`navigation:language.${selectedLanguage.abbreviation}`)
    : null;
};

/**
 * This function sorts an array of objects by their language.
 *
 * @param  a - object a for comparison (LanguageInfo uses `code`, Report uses `language`)
 * @param  b - object b for comparison (LanguageInfo uses `code`, Report uses `language`)
 * @param priorityLanguage - language id of prioritized language
 * @return  - sorting order
 */
export const sortByLanguageName = <T extends LanguageInfo | Report>(
  a: T,
  b: T,
  priorityLanguage = LANGUAGE.EN,
) => {
  const getLanguageId = (item: T): number =>
    'code' in item ? item.code : item.language;

  const LANGUAGE_A = (getLanguageName(getLanguageId(a)) || '').toUpperCase();
  const LANGUAGE_B = (getLanguageName(getLanguageId(b)) || '').toUpperCase();
  const LANGUAGE_PRIORITIZED = (
    getLanguageName(priorityLanguage) || ''
  ).toUpperCase();

  /*
  We are comparing the lexicographical order of language names (strings),
  not the value of numbers.
   */

  if (LANGUAGE_A && LANGUAGE_B) {
    // if LANGUAGE_A is prioritized, reverse the array
    // (i.e. put it in first place)
    if (LANGUAGE_A === LANGUAGE_PRIORITIZED) {
      return -1;
    }
    // if LANGUAGE_B is prioritized, swap places with LANGUAGE_A
    // (i.e. put it in first place)
    if (LANGUAGE_B === LANGUAGE_PRIORITIZED) {
      return 1;
    }
    // if no language is prioritized, but LANGUAGE_B is
    // "greater than" LANGUAGE_A, place LANGUAGE_B first
    if (LANGUAGE_A < LANGUAGE_B) {
      return -1;
    }
    // if no language is prioritized, but LANGUAGE_A is
    // "greater than" LANGUAGE_B, place LANGUAGE_A first
    if (LANGUAGE_A > LANGUAGE_B) {
      return 1;
    }
    // place LANGUAGE_A first by default
    return 1;
  }
  // do not change order
  return 0;
};

/**
 * This function sorts every report with file type .zip to the top!
 */
export const prioritizeZip = (
  a: Report,
  b: Report,
  priorityFileType = ReportFileType.ZIP,
) => {
  const reportFileTypeA = a.file_type;
  if (reportFileTypeA < priorityFileType) return 1;
  if (reportFileTypeA > priorityFileType) return -1;
  return 0;
};

/**
 * @param id - an id for searching the matching name inside a map
 * @param map - an array of objects containing an id and name key
 * @returns - a matching name for id or NULL
 */
export const findInMap = (id: number | string, map: any[]): string | null => {
  const result = map.find((needle) => needle.id === id);

  return typeof result === 'object' &&
    Object.prototype.hasOwnProperty.call(result, 'name')
    ? result.name
    : null;
};

/**
 * Returns the matching module path for the provided module type.
 * @param moduleType - The permission module type
 * @returns - The matching module path or the index path as fallback
 */
export const getModulePath = (moduleType: ModuleType) =>
  modulePaths[moduleType] || routePaths.INDEX;

/**
 * @param id - an id for searching a matching order status inside orderStatusMap
 * @param orderStatusMap - an array of order status objects containing an id and name key
 * @returns - a matching order status
 */
export const getOrderStatus = (id: number, orderStatusMap = ORDER_STATUS_MAP) =>
  findInMap(id, orderStatusMap);

/**
 * @param id - an id for searching a matching report status inside reportStatusMap
 * @param reportStatusMap - an array of report status objects containing an id and name key
 * @returns - a matching report status
 */
export const getReportStatus = (
  id: number,
  reportStatusMap = REPORT_STATUS_MAP,
) => findInMap(id, reportStatusMap);

/**
 * @param id - an id for searching a matching action plan status inside actionPlanStatusMap
 * @param planStatusMap - an array of action plan status objects containing an id and name key
 * @returns - a matching action plan status
 */
export const getActionPlanStatus = (
  id: number,
  planStatusMap = ACTION_PLAN_STATUS_MAP,
) => findInMap(id, planStatusMap);

/**
 * @param dateString
 * @param locale
 * @returns - the formatted date
 */
export const formatDateString = (dateString: string, locale = 'en-GB') => {
  const dateObject = dateString ? new Date(dateString) : undefined;
  // Using Moment for displaying the timeZone because JS only displays the offset
  const timeZoneTranslation = locale === 'en' ? 'CEST' : 'MESZ';

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: 'short',
    timeZone: 'Europe/Berlin',
    year: 'numeric',
  };

  return typeof dateObject === 'object'
    ? `${dateObject.toLocaleTimeString(locale, options)} ${timeZoneTranslation}`
    : null;
};

export const changeItem = (
  array: readonly any[],
  index: number,
  newValue: any,
) => {
  if (index < 0 || array.length < index)
    throw new Error(`illegal index: ${index}. Array length: ${array.length}`);
  if (index === array.length) return [...array, newValue];
  return [...array.slice(0, index), newValue, ...array.slice(index + 1)];
};

export const removeItem = (array: any, index: number) => [
  ...array.slice(0, index),
  ...array.slice(index + 1),
];

export const isNumber = (input: any) =>
  typeof input === 'number' && !Number.isNaN(input);

export const roundOrNull = (input: any) =>
  isNumber(input) ? Math.round(input) : null;

export const numberOrDash = (input: any, addPercent = false) =>
  isNumber(input) ? `${input}${addPercent ? '%' : ''}` : '--';

export const signedNumberOrDash = (input: any) =>
  isNumber(input) ? (input < 0 ? `${input}` : `+${input}`) : '--';

export const internationalDateFormat = (date: Date) =>
  format(date, 'MM/dd/yyyy');

export const hasHorizontalScrollbar = (elementRef: RefObject<HTMLElement>) =>
  !!elementRef?.current &&
  elementRef.current.scrollWidth > elementRef.current.clientWidth;
